/**
 * BCR 子任务(对比区)三态语义 —— fail-semantics(2026-08-26 白波山AY5-TM / task 826 HANDOFF)
 *
 * 后端契约:
 * - stop/failed 子任务 result 被打成结构化失败标记:
 *   {"failed": true, "error": 原因原文, "failed_step": 子任务名}
 * - 变更识别跑通时 result 是多类型 JSON,含 change_details:
 *   {"MCU": url, "change_details": {"modules": [...], "bsw_version": "", "error"?: "..."}}
 *   其中 change_details.error 来自对比任务 comparisonStatus='failed' 的透出。
 *
 * 三态(HANDOFF §1):
 *   失败          → 红 + 原因原文 + 失败所在子任务(+重试)
 *   成功·无变化   → 中性「对比成功,无接口变化」(modules 空且无 error)
 *   成功·有变化   → 组件按现状渲染模块明细/报告链接
 */

import { COMPARISON_RESULT_KEYS } from '@/config/comparisonReportLabels';
import type { BcrSubTask } from '@/types';

export interface SubTaskFailure {
  failed: boolean;
  error?: string;
  failed_step?: string;
}

export interface ChangeDetails {
  modules?: unknown[];
  bsw_version?: string;
  error?: string;
}

export interface ParseResultInfo {
  isSingle: boolean;
  url?: string;
  links?: Record<string, unknown>;
  failure?: SubTaskFailure;
  changeDetails?: ChangeDetails;
}

/** 解析子任务 result:区分 单URL / 多类型JSON / 结构化失败标记。 */
export function parseSubTaskResult(result: string | null): ParseResultInfo {
  if (!result) return { isSingle: false };
  try {
    const parsed = JSON.parse(result);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      if (parsed.failed === true) {
        return { isSingle: false, failure: parsed as SubTaskFailure };
      }
      const hasComparisonKeys = COMPARISON_RESULT_KEYS.some((k) => k in parsed);
      if (hasComparisonKeys) {
        return {
          isSingle: false,
          links: parsed as Record<string, unknown>,
          changeDetails: parsed.change_details as ChangeDetails | undefined,
        };
      }
      // soc/mcu testing 结果:{"link": "<report_url>", "summary": "..."}
      // link 不在 COMPARISON_RESULT_KEYS 中,需显式提取作为单个报告链接
      if (typeof parsed.link === 'string' && parsed.link.startsWith('http')) {
        return { isSingle: true, url: parsed.link };
      }
      // 兜底:JSON 对象中任一 http URL 值(与后端 _extract_report_url 行为一致)
      for (const value of Object.values(parsed)) {
        if (typeof value === 'string' && value.startsWith('http')) {
          return { isSingle: true, url: value };
        }
      }
    }
  } catch {
    // 非 JSON → 按单个 URL 处理
  }
  return { isSingle: true, url: result };
}

export type CompareAreaState =
  | { kind: 'failure'; reason: string; failedStep: string }
  | { kind: 'upstream-stopped'; stepName: string }
  | { kind: 'success-empty' }
  | { kind: 'success-changes'; moduleCount: number }
  | { kind: 'none' };

/**
 * 计算某个子任务对比区的三态。
 * - 自身 result 是失败标记 → failure
 * - 变更识别被上游 stop 卡住(pending/running)→ upstream-stopped
 * - 变更识别 finished:change_details.error → failure;
 *   modules 有值 → success-changes;空 → success-empty
 * - 其余(旧格式/无 change_details)→ none,按现状只渲染链接
 */
export function getCompareAreaState(item: BcrSubTask, subTasks: BcrSubTask[]): CompareAreaState {
  const parsed = parseSubTaskResult(item.result);

  if (parsed.failure) {
    return {
      kind: 'failure',
      reason: parsed.failure.error || `${item.name} 中止`,
      failedStep: parsed.failure.failed_step || item.name,
    };
  }

  if (item.name === 'change identification') {
    if (item.status === 'pending' || item.status === 'running') {
      const stoppedUpstream = subTasks.find(
        (t) => t.name !== item.name && t.status === 'stop',
      );
      if (stoppedUpstream) {
        return { kind: 'upstream-stopped', stepName: stoppedUpstream.name };
      }
    }
    if (item.status === 'finished') {
      const cd = parsed.changeDetails;
      if (cd) {
        if (cd.error) {
          return { kind: 'failure', reason: cd.error, failedStep: item.name };
        }
        const count = (cd.modules ?? []).length;
        return count > 0 ? { kind: 'success-changes', moduleCount: count } : { kind: 'success-empty' };
      }
    }
  }

  return { kind: 'none' };
}